const { jQuery, store } = window;

class PostForms {
  constructor() {
    // 保存する件数の上限
    this.maxToStore = 10;
    this.restore();
  }

  /**
   * Internal: 指定されたキーに対応するフォームデータを返します。
   * 対応するフォームデータが存在しない場合は空のオブジェクトを返します。
   */
  getFormData(key) {
    const idx = this.getIndexOfFormData(key);
    return idx > -1 ? this.forms[idx] : {};
  }

  /**
   * Internal: 指定されたキーでフォームデータを保存します。
   * 保存する際に formData._key にキーが、formData._timestamp に現在の
   * タイムスタンプが設定されます。
   */
  setFormData(key, formData) {
    /* eslint-disable no-underscore-dangle */
    const newFormData = formData;
    newFormData._key = key;
    newFormData._timestamp = new Date().getTime();
    /* eslint-enable no-underscore-dangle */
    const idx = this.getIndexOfFormData(key);
    if (idx > -1) {
      this.forms[idx] = newFormData;
    } else {
      this.forms.push(newFormData);
    }
    // eslint-disable-next-line arrow-body-style
    this.forms = this.forms.sort((a, b) => {
      // eslint-disable-next-line no-underscore-dangle
      return -a._timestamp < -b._timestamp ? -1 : 1;
    });
    this.trimPostForms();
    this.store();
    return newFormData;
  }

  /**
   * Internal: 指定されたキーに対応するフォームデータを削除します。
   */
  deleteFormData(key) {
    // eslint-disable-next-line no-underscore-dangle
    this.forms = this.forms.filter((formData) => formData._key !== key);
    this.store();
  }

  /**
   * Internal: Local Storage内のデータを読み込みます。
   */
  restore() {
    const data = store.get("post_forms");
    let forms = data ? JSON.parse(data) : [];

    // Delete invalid entry which created by bug
    if (forms.length > 0) {
      // eslint-disable-next-line no-underscore-dangle
      forms = forms.filter((obj) => obj._key !== "undefined_undefined");
    }

    this.forms = forms;
  }

  /**
   * Internal: データをLocal Storageに保存します。
   */
  store() {
    store.set("post_forms", JSON.stringify(this.forms));
  }

  /**
   * Internal: 指定されたキーに対応するデータのインデックスを返します。
   * データが存在しない場合は -1 を返します。
   */
  getIndexOfFormData(key) {
    // eslint-disable-next-line no-underscore-dangle
    return this.forms.findIndex(
      (formData) => formData != null && formData._key === key,
    );
  }

  /**
   * Internal: 10件を超えるデータのうち古いものを削除します。
   */
  trimPostForms() {
    if (this.forms.length > this.maxToStore) {
      this.forms = this.forms.slice(0, this.maxToStore);
    }
  }
}

/**
 * 投稿フォーム保存コンポーネント
 *
 * store.jsをラップして、投稿フォームの入力内容をLocal Storageに保存します。
 *
 * 各投稿フォームの入力内容は、 postform_cache をキーとして以下のような構造で
 * Local Storageに保存されます。
 *
 *   [
 *     {
 *       _key: 'Episode_58340',
 *       _timestamp: 1410520725828,
 *       body: '本文',
 *       watched: true,
 *       rating: 3,
 *       channel: 'tv',
 *       spoiler: false
 *     },
 *     {
 *       _key: 'Title_2345',
 *       _timestamp: 1410520725830,
 *       body: '本文',
 *       spoiler: false
 *     },
 *   ]
 *
 * 各投稿フォームの入力内容は投稿が完了した時点で削除されます。
 * また、保存の上限数を超えた場合もタイムスタンプの古いものから
 * 削除されていきます。
 *
 * Examples
 *
 *   new PostFormStorage({)
 *     formSelector: '#js-post-form',
 *     bodySelector: '.js-post-form__body',
 *     watchedSelector: '#js-post-form__watched-fields-toggle',
 *     rating: '#js-post-form__rating',
 *     channel: '#js-post-form__channel',
 *     spoiler: '.js-post-form__spoiler',
 *   });
 */
export default class PostFormStorage {
  /**
   * Constructor.
   *
   * options - 以下のようなプロパティを持つオブジェクト。
   *           subjectType     - 投稿対象の種別。
   *           subjectId       - 投稿対象のID。
   *           formSelector    - 対象とするFORM要素のセレクタ。
   *           bodySelector    - 本文が入力されるTEXTAREA要素のセレクタ。
   *           watchedSelector - 視聴を記録するためのINPUT[type=checkbox]要素のセレクタ。
   *           ratingSelector  - 評価を入力するためのSELECT要素のセレクタ。
   *           channelSelector - 視聴メディアを入力するためのSELECT要素のセレクタ。
   *           spoilerSelector - ネタバレフラグのINPUT[type=checkbox]要素のセレクタ。
   */
  constructor(options) {
    this.options = options;
    this.subjectType = this.options.subjectType;
    this.subjectId = this.options.subjectId;
    this.form = jQuery(this.options.formSelector);
    this.body = jQuery(this.options.bodySelector);
    this.watched = jQuery(this.options.watchedSelector);
    this.rating = jQuery(this.options.ratingSelector);
    this.channel = jQuery(this.options.channelSelector);
    this.spoiler = jQuery(this.options.spoilerSelector);

    // ストレージに保存するデータのキー
    this.key = `${this.subjectType}_${this.subjectId}`;

    this.postForms = new PostForms();
    this.formData = this.postForms.getFormData(this.key);
  }

  init() {
    this.bindEvents();
    this.restoreForm();
  }

  /**
   * Internal: イベントハンドラを設定します。
   */
  bindEvents() {
    this.body.on("input", () => {
      this.formData.body = this.body.val();
      this.formData = this.postForms.setFormData(this.key, this.formData);
    });

    this.watched.on("change", () => {
      this.formData.watched = this.watched.is(":checked");
      this.formData = this.postForms.setFormData(this.key, this.formData);
    });

    this.rating.on("change", () => {
      this.formData.rating = this.rating.val();
      this.formData = this.postForms.setFormData(this.key, this.formData);
    });

    this.channel.on("change", () => {
      this.formData.channel = this.channel.val();
      this.formData = this.postForms.setFormData(this.key, this.formData);
    });

    this.spoiler.on("change", () => {
      this.formData.spoiler = this.spoiler.is(":checked");
      this.formData = this.postForms.setFormData(this.key, this.formData);
    });

    this.form.on("submit reset", () => {
      this.postForms.deleteFormData(this.key);
      this.formData = {};
    });
  }

  /**
   * Internal: データをフォームに復元します。
   */
  restoreForm() {
    if (this.formData.body) {
      this.body.val(this.formData.body);
    }
    if (this.formData.watched) {
      this.watched.prop("checked", this.formData.watched);
    }
    if (this.formData.rating) {
      // Convert old rating value of Post model
      // TODO :Remove
      let value = this.formData.rating;
      if (value === "0") {
        value = "none";
      } else if (value === "1") {
        value = "very_bad";
      } else if (value === "2") {
        value = "bad";
      } else if (value === "3") {
        value = "average";
      } else if (value === "4") {
        value = "good";
      } else if (value === "5") {
        value = "great";
      }
      this.rating.val(value);
    }
    if (this.formData.channel) {
      this.channel.val(this.formData.channel);
    }
    if (this.formData.spoiler) {
      this.spoiler.prop("checked", this.formData.spoiler);
    }
  }
}
